import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://uandfashion.eu';

const Paragraphs = ({ text }) => {
  if (!text) return null;
  return text.split('\n\n').map((para, i) => (
    <p key={i} className="text-gray-700 leading-relaxed mb-4 whitespace-pre-line">
      {para}
    </p>
  ));
};

const BrandSpotlightPage = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [spotlight, setSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await axios.get(`${API}/spotlights/${slug}`);
        setSpotlight(res.data);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setNotFound(true);
        } else {
          console.error('Failed to load spotlight:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [slug]);

  const pick = (nl, en) => (language === 'nl' ? nl : en);

  const jsonLd = useMemo(() => {
    if (!spotlight) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `Ontdek het merk: ${spotlight.brand_name}`,
      author: { '@type': 'Organization', name: 'U&Fashion' },
      publisher: { '@type': 'Organization', name: 'U&Fashion' },
      datePublished: spotlight.publish_date,
      about: { '@type': 'Brand', name: spotlight.brand_name },
      mainEntityOfPage: `${SITE_URL}/merken/${spotlight.slug}`,
    };
  }, [spotlight]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (notFound || !spotlight) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-2xl font-light text-gray-900 mb-4">
            {t('Merkverhaal niet gevonden', 'Brand story not found')}
          </h1>
          <Link to="/ontdek-merken" className="text-pink-500 hover:underline">
            {t('← Terug naar alle merken', '← Back to all brands')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="brand-spotlight-page">
      <SEO
        title={t(
          `Ontdek het merk: ${spotlight.brand_name} | U&Fashion`,
          `Discover the brand: ${spotlight.brand_name} | U&Fashion`
        )}
        description={pick(spotlight.excerpt_nl, spotlight.excerpt_en)}
        canonical={`${SITE_URL}/merken/${spotlight.slug}`}
        jsonLd={jsonLd}
      />

      {/* Back link */}
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <Link
          to="/ontdek-merken"
          className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 transition-colors"
          data-testid="back-to-brands"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('Terug naar alle merken', 'Back to all brands')}
        </Link>
      </div>

      {/* Header */}
      <article className="max-w-3xl mx-auto px-6 py-12">
        {spotlight.featured && (
          <p className="text-xs uppercase tracking-[0.3em] text-pink-500 mb-3" data-testid="featured-tag">
            {t('Nu in de kijker', 'Currently featured')}
          </p>
        )}
        <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 tracking-tight">
          {t(`Ontdek het merk: ${spotlight.brand_name}`, `Discover the brand: ${spotlight.brand_name}`)}
        </h1>

        {spotlight.logo_image && (
          <div className="mb-10 inline-block bg-gray-50 px-6 py-4 rounded-lg border border-gray-100">
            <img
              src={spotlight.logo_image}
              alt={`${spotlight.brand_name} logo`}
              className="h-10 md:h-12 w-auto object-contain"
              loading="lazy"
            />
          </div>
        )}

        {spotlight.hero_image && (
          <div className="mb-12 -mx-6 md:mx-0 md:rounded-2xl overflow-hidden">
            <img
              src={spotlight.hero_image}
              alt={`${spotlight.brand_name} editorial`}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        )}

        {/* Sections */}
        <section className="mb-10">
          <Paragraphs text={pick(spotlight.intro_nl, spotlight.intro_en)} />
        </section>

        <section className="mb-10">
          <h2 className="text-xl md:text-2xl font-medium text-gray-900 mb-4">
            {t('Het verhaal', 'The story')}
          </h2>
          <Paragraphs text={pick(spotlight.story_nl, spotlight.story_en)} />
        </section>

        <section className="mb-10">
          <h2 className="text-xl md:text-2xl font-medium text-gray-900 mb-4">
            {t('Stijl en uitstraling', 'Style and identity')}
          </h2>
          <Paragraphs text={pick(spotlight.style_nl, spotlight.style_en)} />
        </section>

        <section className="mb-12">
          <h2 className="text-xl md:text-2xl font-medium text-gray-900 mb-4">
            {t('Waarom dit merk interessant is', 'Why this brand is interesting')}
          </h2>
          <Paragraphs text={pick(spotlight.opinion_nl, spotlight.opinion_en)} />
        </section>

        {/* CTA */}
        <div className="pt-8 border-t border-gray-100">
          <a
            href={spotlight.collection_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
            data-testid="brand-collection-cta"
          >
            {t('Bekijk de collectie', 'View the collection')}
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-4 text-xs text-gray-400">
            {t(
              'Sommige links zijn affiliate links. Dat kost jou niets extra.',
              'Some links are affiliate links. This costs you nothing extra.'
            )}
          </p>
        </div>
      </article>
    </div>
  );
};

export default BrandSpotlightPage;
